import { CloseRounded, PictureAsPdfRounded, TableChartRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  TextField as MuiTextField,
  Typography,
  styled,
} from "@mui/material";
import React from "react";
import { black, green, greenLight, greyEnabled, orange, orangeLightActive, purple, purpleLight } from "../../../../theme/colors";

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function DownloadPopup({
  open,
  format,
  onDialogClose,
  onSubmit,
  submitting = false,
}: {
  open: boolean;
  format?: "excel" | "pdf";
  onDialogClose: () => void;
  onSubmit: (fromPage: number | null, toAge: number | null) => void;
  submitting?: boolean;
}) {
  const [fromPage, setFromPage] = React.useState<string | null>(null);
  const [toPage, setToPage] = React.useState<string | null>(null);

  const handleClose = () => {
    if (!submitting) onDialogClose();
  };

  const rangeInvalid =
    !!fromPage && !!toPage && +fromPage > 0 && +toPage > 0 && +toPage < +fromPage;
  const canSubmit = !!(+fromPage! && +toPage!) && !rangeInvalid;

  const isPdf = format === "pdf";
  const accentColor = format ? (isPdf ? orange : green) : purple;
  const accentBg = format ? (isPdf ? orangeLightActive : greenLight) : purpleLight;
  const title = format ? `Download ${isPdf ? "PDF" : "Excel"} Report` : "Download Report";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <Box sx={{ width: 440, maxWidth: "90vw" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: accentBg,
                flexShrink: 0,
              }}
            >
              {isPdf ? (
                <PictureAsPdfRounded sx={{ color: accentColor, fontSize: 22 }} />
              ) : (
                <TableChartRounded sx={{ color: accentColor, fontSize: 22 }} />
              )}
            </Box>
            <Typography variant="h6" fontWeight={700} color={black}>
              {title}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" disabled={submitting}>
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
          <Typography variant="body2" fontWeight={700} color={black} mb={2}>
            Enter the page range you want to include in the report.
          </Typography>
          <Box display="flex" flexDirection="row" gap={2}>
            <TextField
              label="From Page"
              type="number"
              size="small"
              fullWidth
              disabled={submitting}
              error={rangeInvalid}
              value={fromPage ?? ""}
              onChange={({ target: { value } }) => {
                setFromPage(value);
              }}
            />
            <TextField
              label="To Page"
              type="number"
              size="small"
              fullWidth
              disabled={submitting}
              error={rangeInvalid}
              value={toPage ?? ""}
              onChange={({ target: { value } }) => {
                setToPage(value);
              }}
            />
          </Box>
          {rangeInvalid && (
            <Typography variant="caption" color="error" mt={0.5} display="block">
              "To Page" must be greater than or equal to "From Page".
            </Typography>
          )}

          <Box display="flex" justifyContent="space-between" gap={1.5} mt={4}>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={submitting}
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              disabled={submitting || !canSubmit}
              startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
              onClick={() => onSubmit(+fromPage!, +toPage!)}
              sx={{
                minWidth: 160,
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              {submitting ? "Downloading..." : "Download"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
